import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calendar } from "../components/ui/calendar";
import { ArrowLeft, ArrowRight, Clock, Users, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import WhatsAppFloat from "../components/WhatsAppFloat";
import { useSite } from "../lib/SiteContext";
import { fetchAvailability, createBooking, mediaUrl } from "../lib/api";

const Step = ({ n, label, active, done }) => (
  <div className="flex items-center gap-3">
    <div
      className={`w-8 h-8 flex items-center justify-center text-xs font-medium border ${
        active
          ? "border-primary bg-primary text-white"
          : done
          ? "border-secondary bg-secondary text-white"
          : "border-border text-muted-foreground"
      }`}
      style={{ borderRadius: "2px" }}
    >
      {done ? <Check size={14} /> : n}
    </div>
    <span className={`text-xs uppercase tracking-[0.2em] ${active ? "text-primary" : "text-muted-foreground"}`}>
      {label}
    </span>
  </div>
);

const BookingPage = () => {
  const { charters } = useSite();
  const [params] = useSearchParams();
  const preselected = params.get("charter");

  const [step, setStep] = useState(1);
  const [charterId, setCharterId] = useState(preselected || "");
  const [date, setDate] = useState(undefined);
  const [guests, setGuests] = useState(2);
  const [details, setDetails] = useState({ name: "", email: "", phone: "", notes: "" });
  const [paymentType, setPaymentType] = useState("deposit");
  const [blocked, setBlocked] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (charterId) {
      fetchAvailability(charterId)
        .then((d) => setBlocked(d.blocked_dates || []))
        .catch(() => setBlocked([]));
    } else {
      fetchAvailability().then((d) => setBlocked(d.blocked_dates || [])).catch(() => {});
    }
  }, [charterId]);

  useEffect(() => {
    if (preselected && !charterId) setCharterId(preselected);
  }, [preselected, charterId]);

  const charter = useMemo(() => charters.find((c) => c.id === charterId), [charters, charterId]);
  const isPartner = charter?.type === "partner";
  const blockedDateObjs = useMemo(
    () => blocked.map((d) => new Date(d + "T12:00:00")),
    [blocked]
  );

  const next = () => {
    if (step === 1 && !charterId) return toast.error("Please select a charter");
    if (step === 2 && !date) return toast.error("Please select a date");
    if (step === 3) {
      if (!details.name || !details.email || !details.phone) {
        return toast.error("Please fill in your name, email and phone");
      }
      if (guests < 1 || (charter && guests > charter.max_guests)) {
        return toast.error(`Guests must be between 1 and ${charter?.max_guests || 8}`);
      }
    }
    setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!charter || !date) return;
    setSubmitting(true);
    try {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      const res = await createBooking({
        charter_id: charter.id,
        booking_date: dateStr,
        guests: Number(guests),
        customer_name: details.name,
        customer_email: details.email,
        customer_phone: details.phone,
        notes: details.notes,
        payment_type: paymentType,
        origin_url: window.location.origin,
      });
      if (res.awaiting_approval) {
        window.location.href = `/booking/success?awaiting_approval=1&booking_id=${res.booking.id}`;
      } else if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Booking failed. Please try again.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const amountDue =
    charter && (paymentType === "deposit" ? charter.deposit_eur : charter.price_eur);

  return (
    <div data-testid="booking-page">
      <Navigation />
      <div className="pt-32 pb-20 bg-background min-h-screen">
        <div className="container-x">
          <div className="max-w-3xl mb-10">
            <div className="overline mb-3">Online Booking</div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-primary text-balance">
              Reserve your day at sea
            </h1>
            <p className="text-muted-foreground mt-4">
              Pick your charter, choose a date, and pay securely with Stripe — full refund up to 48h before departure.
            </p>
          </div>

          <div className="flex flex-wrap gap-4 md:gap-8 mb-10" data-testid="booking-stepper">
            <Step n={1} label="Charter" active={step === 1} done={step > 1} />
            <Step n={2} label="Date" active={step === 2} done={step > 2} />
            <Step n={3} label="Details" active={step === 3} done={step > 3} />
            <Step n={4} label="Confirm" active={step === 4} done={false} />
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-8 bg-white border border-border p-6 md:p-10">
              {step === 1 && (
                <div data-testid="step-charter">
                  <h2 className="font-serif text-2xl md:text-3xl text-primary mb-6">Choose your charter</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {charters.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => setCharterId(c.id)}
                        className={`text-left p-5 border transition-all ${
                          charterId === c.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                        }`}
                        data-testid={`select-charter-${c.slug}`}
                      >
                        <div className="aspect-[16/10] overflow-hidden mb-4">
                          <img src={mediaUrl(c.image_url)} alt={c.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="text-xs uppercase tracking-[0.2em] text-secondary mb-1">
                          {c.duration_hours}h · {c.start_time}–{c.end_time}
                        </div>
                        <div className="font-serif text-xl text-primary">{c.title}</div>
                        <div className="text-sm text-muted-foreground mb-3">{c.subtitle}</div>
                        <div className="font-serif text-2xl text-primary">€{Number(c.price_eur).toLocaleString()}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="step-date">
                  <h2 className="font-serif text-2xl md:text-3xl text-primary mb-6">Pick a date</h2>
                  <div className="flex justify-center">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (d < today) return true;
                        const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                        return blocked.includes(ds);
                      }}
                      modifiers={{ unavailable: blockedDateObjs }}
                      modifiersClassNames={{ unavailable: "line-through opacity-40" }}
                      className="border border-border p-4"
                    />
                  </div>
                  <div className="text-xs text-muted-foreground mt-4 text-center">
                    Crossed-out dates are unavailable (booked via our site or another platform).
                  </div>
                </div>
              )}

              {step === 3 && (
                <div data-testid="step-details">
                  <h2 className="font-serif text-2xl md:text-3xl text-primary mb-6">Your details</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input
                      className="input-field"
                      placeholder="Full name"
                      value={details.name}
                      onChange={(e) => setDetails({ ...details, name: e.target.value })}
                      data-testid="booking-name"
                    />
                    <input
                      type="email"
                      className="input-field"
                      placeholder="Email"
                      value={details.email}
                      onChange={(e) => setDetails({ ...details, email: e.target.value })}
                      data-testid="booking-email"
                    />
                    <input
                      className="input-field"
                      placeholder="Phone (with country code)"
                      value={details.phone}
                      onChange={(e) => setDetails({ ...details, phone: e.target.value })}
                      data-testid="booking-phone"
                    />
                    <input
                      type="number"
                      min={1}
                      max={charter?.max_guests || 8}
                      className="input-field"
                      placeholder="Guests"
                      value={guests}
                      onChange={(e) => setGuests(e.target.value)}
                      data-testid="booking-guests"
                    />
                  </div>
                  <textarea
                    className="input-field mt-4 min-h-[110px] resize-none"
                    placeholder="Anything we should know? (allergies, special occasion, preferred route…)"
                    value={details.notes}
                    onChange={(e) => setDetails({ ...details, notes: e.target.value })}
                    data-testid="booking-notes"
                  />

                  <div className="mt-8">
                    <h3 className="font-serif text-xl text-primary mb-3">Payment option</h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <button
                        onClick={() => setPaymentType("deposit")}
                        className={`p-4 text-left border ${
                          paymentType === "deposit" ? "border-primary bg-primary/5" : "border-border"
                        }`}
                        data-testid="pay-deposit"
                      >
                        <div className="font-medium text-primary">Pay deposit now</div>
                        <div className="text-sm text-muted-foreground">
                          €{Number(charter?.deposit_eur || 0).toLocaleString()} now · balance on the day
                        </div>
                      </button>
                      <button
                        onClick={() => setPaymentType("full")}
                        className={`p-4 text-left border ${
                          paymentType === "full" ? "border-primary bg-primary/5" : "border-border"
                        }`}
                        data-testid="pay-full"
                      >
                        <div className="font-medium text-primary">Pay in full now</div>
                        <div className="text-sm text-muted-foreground">
                          €{Number(charter?.price_eur || 0).toLocaleString()} total · skip payment on the day
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && charter && (
                <div data-testid="step-confirm">
                  <h2 className="font-serif text-2xl md:text-3xl text-primary mb-6">Review &amp; confirm</h2>
                  <div className="space-y-4 text-sm">
                    <div className="flex justify-between border-b border-border py-3">
                      <span className="text-muted-foreground">Charter</span>
                      <span className="font-medium text-primary">{charter.title}</span>
                    </div>
                    <div className="flex justify-between border-b border-border py-3">
                      <span className="text-muted-foreground">Date</span>
                      <span className="font-medium text-primary">
                        {date?.toLocaleDateString(undefined, {
                          weekday: "short",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-border py-3">
                      <span className="text-muted-foreground">Time</span>
                      <span className="font-medium text-primary">
                        {charter.start_time}–{charter.end_time}
                      </span>
                    </div>
                    <div className="flex justify-between border-b border-border py-3">
                      <span className="text-muted-foreground">Guests</span>
                      <span className="font-medium text-primary">{guests}</span>
                    </div>
                    <div className="flex justify-between border-b border-border py-3">
                      <span className="text-muted-foreground">Name</span>
                      <span className="font-medium text-primary">{details.name}</span>
                    </div>
                    <div className="flex justify-between border-b border-border py-3">
                      <span className="text-muted-foreground">Contact</span>
                      <span className="font-medium text-primary text-right">
                        {details.email}
                        <br />
                        {details.phone}
                      </span>
                    </div>
                    <div className="flex justify-between py-3">
                      <span className="text-muted-foreground">Total charter price</span>
                      <span className="font-serif text-2xl text-primary">
                        €{Number(charter.price_eur).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between py-3 bg-primary/5 px-4 -mx-4">
                      <span className="text-primary font-medium">
                        {isPartner ? "Deposit on approval" : `Pay now (${paymentType === "deposit" ? "deposit" : "full"})`}
                      </span>
                      <span className="font-serif text-3xl text-primary">€{Number(amountDue).toLocaleString()}</span>
                    </div>
                    {isPartner && (
                      <div className="mt-4 text-sm bg-secondary/5 border border-secondary/20 p-4 text-primary" data-testid="partner-notice">
                        <strong>This is a partner charter.</strong> No payment is taken now. We&rsquo;ll check availability
                        with the boat owner and email you a secure payment link (usually within 24h). If the partner isn&rsquo;t
                        available for your date, no charge is made.
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-10 pt-6 border-t border-border">
                {step > 1 ? (
                  <button onClick={back} className="btn-outline" data-testid="booking-back">
                    <ArrowLeft size={14} /> Back
                  </button>
                ) : (
                  <Link to="/" className="btn-outline">
                    <ArrowLeft size={14} /> Home
                  </Link>
                )}
                {step < 4 ? (
                  <button onClick={next} className="btn-primary" data-testid="booking-next">
                    Continue <ArrowRight size={14} />
                  </button>
                ) : (
                  <button onClick={submit} disabled={submitting} className="btn-primary" data-testid="booking-pay">
                    {submitting ? (
                      <><Loader2 size={14} className="animate-spin" /> {isPartner ? "Sending request…" : "Redirecting…"}</>
                    ) : isPartner ? (
                      <>Send booking request <ArrowRight size={14} /></>
                    ) : (
                      <>Pay €{Number(amountDue).toLocaleString()} <ArrowRight size={14} /></>
                    )}
                  </button>
                )}
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="sticky top-28 bg-primary text-white p-7" data-testid="booking-summary">
                <div className="overline text-sand mb-3">Your charter</div>
                <h3 className="font-serif text-2xl leading-tight mb-4">{charter ? charter.title : "Select a charter"}</h3>
                {charter && (
                  <>
                    <div className="aspect-[4/3] overflow-hidden mb-5">
                      <img src={mediaUrl(charter.image_url)} alt={charter.title} className="w-full h-full object-cover" />
                    </div>
                    <ul className="space-y-2 text-sm text-white/85">
                      <li className="flex items-center gap-2">
                        <Clock size={14} /> {charter.duration_hours}h · {charter.start_time}–{charter.end_time}
                      </li>
                      <li className="flex items-center gap-2">
                        <Users size={14} /> Up to {charter.max_guests} guests
                      </li>
                      {date && (
                        <li className="flex items-center gap-2">
                          <Check size={14} /> {date.toLocaleDateString()}
                        </li>
                      )}
                    </ul>
                    <div className="hairline bg-white/15 my-5" />
                    <div className="flex justify-between items-end">
                      <span className="text-xs uppercase tracking-[0.2em] text-white/70">From</span>
                      <span className="font-serif text-3xl">€{Number(charter.price_eur).toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>
            </aside>
          </div>
        </div>
      </div>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default BookingPage;
